import { ChargeControl } from '../../components/ChargeControl';
import { elementSymbol } from '@peruselab/structure-editor/chemistry/elements';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERIODIC_TABLE, type PeriodicTableCell } from '@peruselab/structure-editor/chemistry/periodicTable';
import { Icon } from '../../components/Icon';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface PeriodicTablePanelProps {
  readonly open: boolean;
  readonly armedZ: number | null;
  readonly onPick: (atomicNumber: number) => void;
  readonly onClose: () => void;
  readonly charge?: number;
  readonly onChargeChange?: (value: number) => void;
  // #654 (Markush atom lists) -- multi-select: a tap toggles an element in `selected` instead of
  // arming it and closing; Done (two or more chosen) hands the set back.
  readonly multiSelect?: {
    readonly selected: readonly number[];
    readonly onToggle: (atomicNumber: number) => void;
    readonly onDone: () => void;
  };
}

// Rows 8/9 (the f-block strip) render at grid rows 9/10, leaving row 8 as a spacer. The
// touch-to-zoom window works in this *rendered* row space so its 3x3 matches what the eye sees.
function gridRowOf(cell: PeriodicTableCell): number {
  return cell.period >= 8 ? cell.period + 1 : cell.period;
}

// #411 -- the 18-column grid is too tight to tap accurately on a phone. On a coarse pointer /
// narrow viewport the first tap picks a *centre* instead of selecting; we then float a 3x3
// inset of full-size targets around it and the tap inside the inset makes the real selection.
function zoomShouldEngage(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(max-width: 720px)').matches || window.matchMedia('(pointer: coarse)').matches;
}

// #255 -- a full periodic table. Picking an element arms it (`element-${z}` tool) and closes the
// panel; the user then clicks atoms on the canvas to set their symbol. Centred modal on the
// File-drawer backdrop pattern: Escape and a backdrop click dismiss, focus starts on Search.
export function PeriodicTablePanel({
  open,
  armedZ,
  onPick,
  onClose,
  multiSelect,
  charge = 0,
  onChargeChange
}: PeriodicTablePanelProps) {
  const { t } = useTranslation();
  const searchRef = useRef<HTMLInputElement | null>(null);
  const zoomBackRef = useRef<HTMLButtonElement | null>(null);
  const [query, setQuery] = useState('');
  // Atomic number the touch-to-zoom inset is centred on; null when the inset is closed.
  const [zoomCentreZ, setZoomCentreZ] = useState<number | null>(null);
  const normalizedQuery = query.trim().toLowerCase();

  const matchesQuery = (cell: PeriodicTableCell): boolean =>
    cell.name.toLowerCase().startsWith(normalizedQuery) || cell.symbol.toLowerCase().startsWith(normalizedQuery);

  const zoom = useMemo(() => {
    if (zoomCentreZ === null) return null;
    const centre = PERIODIC_TABLE.find((cell) => cell.z === zoomCentreZ);
    if (!centre) return null;
    const centreRow = gridRowOf(centre);
    const cells = PERIODIC_TABLE.filter(
      (cell) => Math.abs(gridRowOf(cell) - centreRow) <= 1 && Math.abs(cell.column - centre.column) <= 1
    );
    return { centre, centreRow, cells };
  }, [zoomCentreZ]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setZoomCentreZ(null);
    searchRef.current?.focus();
  }, [open]);

  // Escape backs out of the zoom inset first, then closes the whole panel.
  useEscapeKey(open, () => {
    if (zoomCentreZ !== null) setZoomCentreZ(null);
    else onClose();
  });

  // Move focus into the inset when it opens so a keyboard user is not left on the grid behind it.
  useEffect(() => {
    if (zoomCentreZ !== null) zoomBackRef.current?.focus();
  }, [zoomCentreZ]);

  if (!open) return null;

  const isActive = (z: number) => (multiSelect ? multiSelect.selected.includes(z) : armedZ === z);
  // Multi-select toggles and keeps the panel open (closing the zoom inset so the grid shows the
  // new state); single-select arms and closes, as before.
  const choose = (z: number) => {
    if (!multiSelect) {
      onPick(z);
      return;
    }
    multiSelect.onToggle(z);
    setZoomCentreZ(null);
  };
  const title = multiSelect ? t('workspace.periodicTable.listTitle') : t('workspace.periodicTable.title');

  // On a coarse pointer the first tap on a cell opens the zoom inset centred on that cell (the
  // tapped cell *is* the element nearest the touch); a second tap -- inside the inset -- selects.
  const handleCellTap = (z: number) => {
    if (zoomCentreZ === null && zoomShouldEngage()) setZoomCentreZ(z);
    else choose(z);
  };

  return (
    <div
      className="workspace-drawer-backdrop periodic-table-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="periodic-table-panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="periodic-table-panel"
      >
        <div className="periodic-table-head">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label={t('workspace.periodicTable.close')}
            title={t('workspace.periodicTable.close')}
          >
            <Icon name="close" />
          </button>
        </div>
        {multiSelect ? (
          <div className="periodic-table-list">
            <p className="periodic-table-hint">{t('workspace.periodicTable.listHint')}</p>
            <output className="periodic-table-list-value" data-testid="atom-list-draft" aria-live="polite">
              {multiSelect.selected.length > 0
                ? `[${multiSelect.selected.map(elementSymbol).join(',')}]`
                : t('workspace.periodicTable.listEmpty')}
            </output>
            <button
              type="button"
              data-testid="atom-list-done"
              disabled={multiSelect.selected.length < 2}
              onClick={multiSelect.onDone}
            >
              {t('workspace.periodicTable.listDone')}
            </button>
          </div>
        ) : (
          <p className="periodic-table-hint">{t('workspace.periodicTable.hint')}</p>
        )}
        {/* #489: an icon-led pill, matching the top-bar search's visual idiom (TopBar.tsx's
            .workspace-search), rather than a full-width unstyled native <input>. */}
        <div className="periodic-table-search-row">
          <div className="periodic-table-search">
            <Icon name="search" className="periodic-table-search-icon" />
            <input
              ref={searchRef}
              type="search"
              aria-label={t('workspace.periodicTable.search')}
              placeholder={t('workspace.periodicTable.search')}
              value={query}
              onChange={(event) => {
                const value = event.target.value;
                setQuery(value.charAt(0).toUpperCase() + value.slice(1));
              }}
            />
          </div>
          {!multiSelect && onChargeChange ? <ChargeControl value={charge} onChange={onChargeChange} /> : null}
        </div>
        <div className="periodic-table-grid" role="group" aria-label={t('workspace.periodicTable.title')}>
          {PERIODIC_TABLE.map((cell) => (
            <button
              key={cell.z}
              type="button"
              className={`periodic-table-cell ${isActive(cell.z) ? 'active' : ''}`}
              style={{ gridColumn: cell.column, gridRow: gridRowOf(cell) }}
              aria-label={`${cell.symbol} (${cell.z})`}
              title={cell.name}
              disabled={!matchesQuery(cell)}
              aria-pressed={isActive(cell.z)}
              onClick={() => handleCellTap(cell.z)}
            >
              <span className="periodic-table-z">{cell.z}</span>
              <span className="periodic-table-symbol">{cell.symbol}</span>
            </button>
          ))}
        </div>
      </div>

      {zoom && (
        <div
          className="periodic-table-zoom-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setZoomCentreZ(null);
          }}
        >
          <div
            className="periodic-table-zoom"
            role="group"
            aria-label={t('workspace.periodicTable.zoom')}
            data-testid="periodic-table-zoom"
          >
            <div className="periodic-table-zoom-head">
              <span>{t('workspace.periodicTable.zoomHint')}</span>
              <button
                ref={zoomBackRef}
                type="button"
                className="icon-button"
                onClick={() => setZoomCentreZ(null)}
                aria-label={t('workspace.periodicTable.zoomBack')}
                title={t('workspace.periodicTable.zoomBack')}
              >
                <Icon name="close" />
              </button>
            </div>
            <div className="periodic-table-zoom-grid">
              {zoom.cells.map((cell) => (
                <button
                  key={cell.z}
                  type="button"
                  className={`periodic-table-cell ${isActive(cell.z) ? 'active' : ''}`}
                  style={{
                    gridColumn: cell.column - zoom.centre.column + 2,
                    gridRow: gridRowOf(cell) - zoom.centreRow + 2
                  }}
                  aria-label={`${cell.symbol} (${cell.z}) — ${cell.name}`}
                  title={cell.name}
                  disabled={!matchesQuery(cell)}
                  aria-pressed={isActive(cell.z)}
                  onClick={() => choose(cell.z)}
                >
                  <span className="periodic-table-z">{cell.z}</span>
                  <span className="periodic-table-symbol">{cell.symbol}</span>
                  <span className="periodic-table-zoom-name">{cell.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
